import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import seoContent, { getRelatedLinks } from '../config/seoContent';
import { calculatorConfig } from '../config/features';

/**
 * SEOContentSection — crawlable explanatory content rendered below each
 * calculator: how-it-works copy, formula, reference table, FAQs, related
 * calculators, and a CONFORM.TOOLS CTA. Baked into static HTML by react-snap.
 *
 * The FAQ entries rendered here are the same ones SEOHead emits as FAQPage
 * JSON-LD — keep them in sync via config/seoContent.js.
 */
const SEOContentSection = ({ calculatorKey }) => {
  const content = seoContent[calculatorKey];
  if (!content) return null;

  const related = getRelatedLinks(calculatorKey, calculatorConfig);
  const utm = `?utm_source=data-calc&utm_medium=referral&utm_campaign=${encodeURIComponent(calculatorKey)}`;

  return (
    <div className="mt-3 space-y-3 text-left">
      {/* How it works */}
      <section className="px-5 py-4 bg-surface-card rounded-xl border border-border">
        <h2 className="font-headline font-bold text-base text-ink mb-2">
          How this calculator works
        </h2>
        <div className="text-sm text-ink-secondary leading-relaxed space-y-3">
          {content.intro.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>

        {content.formula && (
          <div className="mt-4 px-4 py-3 rounded-lg bg-surface-page border border-border-light">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-1">
              {content.formula.label}
            </p>
            <p className="text-sm font-mono text-ink break-words">{content.formula.expression}</p>
            {content.formula.example && (
              <p className="text-xs text-ink-secondary mt-1.5">{content.formula.example}</p>
            )}
          </div>
        )}

        {content.table && (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <caption className="text-left text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-2">
                  {content.table.caption}
                </caption>
                <thead>
                  <tr className="border-b border-border">
                    {content.table.headers.map((header) => (
                      <th key={header} className="text-left py-2 pr-4 font-semibold text-ink whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.table.rows.map((row, i) => (
                    <tr key={i} className="border-b border-border-light">
                      {row.map((cell, j) => (
                        <td key={j} className="py-2 pr-4 text-ink-secondary">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {content.table.note && (
              <p className="text-xs text-ink-tertiary mt-2">{content.table.note}</p>
            )}
          </div>
        )}
      </section>

      {/* FAQs */}
      {content.faqs && content.faqs.length > 0 && (
        <section className="px-5 py-4 bg-surface-card rounded-xl border border-border">
          <h2 className="font-headline font-bold text-base text-ink mb-3">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {content.faqs.map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-sm font-semibold text-ink mb-1">{q}</h3>
                <p className="text-sm text-ink-secondary leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related calculators */}
      {related.length > 0 && (
        <section className="px-5 py-4 bg-surface-card rounded-xl border border-border">
          <h2 className="font-headline font-bold text-base text-ink mb-3">
            Related calculators
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {related.map(({ key, path, title, description }) => (
              <Link
                key={key}
                to={path}
                className="group p-3 rounded-lg border border-border hover:border-conform-yellow/30 transition-colors no-underline"
              >
                <span className="flex items-center gap-1 text-sm font-semibold text-ink group-hover:text-conform-yellow transition-colors">
                  {title}
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="block text-xs text-ink-secondary leading-relaxed mt-1 line-clamp-2">
                  {description}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CONFORM.TOOLS CTA */}
      <section className="px-5 py-4 bg-surface-card rounded-xl border border-border">
        <p className="text-sm text-ink-secondary leading-relaxed">
          {/* No {' '} here: adjacent JSX text nodes break hydration of
              react-snap HTML (serialized DOM merges them) */}
          DataCalc is built by <a
            href={`https://conform.tools/${utm}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-conform-red hover:text-conform-yellow transition-colors"
          >CONFORM.TOOLS</a> — professional conform and post-production software for editors,
          assistant editors, and finishing artists. If DataCalc saves you time, take a look at
          what else we make.
        </p>
      </section>
    </div>
  );
};

export default SEOContentSection;
